import Skeleton from 'react-loading-skeleton';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import Modal from '../../../../ui-new/Modal/Modal';
import styles from './CreateAdminInvoiceModal.module.css';
import cn from 'classnames';
import { useAdminClientInvoiceData } from './useAdminClientInvoiceData';
import { asMoney } from '../../../../utils/utits';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import Button from '../../../../ui-new/Button/Button';
import { CreateAndSendAdminInvoiceData } from '@round/api';
import { useEffect, useState } from 'react';
import { MAX_TWO_NUMBERS_MAX_TWO_DECIMALS_REGEX } from '../../../booking/helpers';

type Props = {
    data: {
        clientId: number | null;
        bookingId: number;
        bookingCost: number;
        bookingCurrencyId: number;
    } | null;
    isOpen: boolean;
    onClose: () => void;
    onCreateAndSendInvoice: (data: CreateAndSendAdminInvoiceData) => Promise<void>;
    onSaveAsDraft: (discount?: number) => Promise<void>;
};

const CreateAdminInvoiceModal = ({ data, isOpen, onClose, onCreateAndSendInvoice, onSaveAsDraft }: Props) => {
    const [isCreateAndSendLoading, setIsCreateAndSendLoading] = useState(false);
    const [isSaveAsDraftLoading, setIsSaveAsDraftLoading] = useState(false);

    const { currencies } = useNonNullContext(OptionsContext);
    const { client, xeroContact, isInitialized, init, reset } = useAdminClientInvoiceData({
        clientId: data?.clientId ?? null,
    });

    const [discount, setDiscount] = useState(`${0}`);
    const [isDiscountValid, setIsDiscountValid] = useState<boolean | undefined>(true);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (client?.default_invoice_discount) {
            const defaultDiscountAsPercent = client.default_invoice_discount * 100;
            setDiscount(`${defaultDiscountAsPercent}`);
            setIsDiscountValid(MAX_TWO_NUMBERS_MAX_TWO_DECIMALS_REGEX.test(`${defaultDiscountAsPercent}`));
        }
    }, [client, isInitialized]);

    const discountAsNumber = parseFloat(discount);

    useAbortableEffect(
        (signal) => {
            if (isOpen && !isInitialized) {
                init({ signal });
            }
        },
        [isOpen, isInitialized, init]
    );

    const handleClose = () => {
        reset();
        setDiscount(`${0}`);
        onClose();
    };

    const total = data?.bookingCost ? data.bookingCost - data.bookingCost * (discountAsNumber / 100) : null;
    const totalDisplayValue = total
        ? asMoney(
              total,
              currencies.find((c) => c.id === data?.bookingCurrencyId)
          )
        : '-';

    const handleCreateAndSendInvoice = async () => {
        const { bookingId, bookingCost, bookingCurrencyId } = data ?? {};
        if (!bookingId || !bookingCost || !bookingCurrencyId) {
            return;
        }

        if (isDiscountValid === false || (typeof discountAsNumber && isNaN(discountAsNumber))) {
            return;
        }

        setIsCreateAndSendLoading(true);
        await onCreateAndSendInvoice({
            bookings: [bookingId],
            amount: bookingCost,
            currency_id: bookingCurrencyId,
            discount: discountAsNumber > 0 ? discountAsNumber / 100 : undefined,
        });
        setIsCreateAndSendLoading(false);
    };

    const handleSaveAsDraft = async () => {
        if (isDiscountValid === false || (typeof discountAsNumber && isNaN(discountAsNumber))) {
            return;
        }

        setIsSaveAsDraftLoading(true);
        await onSaveAsDraft(discountAsNumber > 0 ? discountAsNumber / 100 : undefined);
        setIsSaveAsDraftLoading(false);
    };

    const handleDiscountChange = (value: string) => {
        const moreThanOneDecimalPoint = value.split('').filter((c) => c === '.').length > 1;
        const moreThanTwoDigitsAsideDecimalPoint = !!value.split('.').find((el) => el.length > 2);
        if (moreThanOneDecimalPoint || moreThanTwoDigitsAsideDecimalPoint) {
            return;
        }

        setDiscount(value);
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className={styles.container} closeOnEscape closeOnOverlayClick>
            <Modal.Header>Invoice</Modal.Header>
            <div>
                <div className={styles.name}>
                    {!isInitialized ? <Skeleton className={styles.clientDetailSkeleton} /> : client?.name ?? '-'}
                </div>

                <div className={cn(styles.mail, { [styles.warning]: !!isInitialized && !xeroContact?.email_address })}>
                    {!isInitialized ? (
                        <Skeleton className={styles.clientDetailSkeleton} />
                    ) : (
                        xeroContact?.email_address ?? 'Email missing'
                    )}
                </div>
            </div>

            <dl className={styles.invoiceDetails}>
                <hr />

                <div className={styles.row}>
                    <dd>Invoice amount</dd>
                    <dt>
                        {asMoney(
                            data?.bookingCost,
                            currencies.find((c) => c.id === data?.bookingCurrencyId)
                        )}
                    </dt>
                </div>

                <div className={styles.row}>
                    <dd>
                        <label htmlFor="discount" id="discount">
                            Discount
                        </label>
                    </dd>
                    <dt>
                        {!isInitialized ? (
                            <Skeleton className={styles.inputSkeleton} />
                        ) : (
                            <div className={cn(styles.inputContainer, { [styles.invalid]: isDiscountValid === false })}>
                                <span className={styles.prefix}>%</span>
                                <input
                                    value={discount}
                                    onChange={(e) => {
                                        setIsDiscountValid(undefined);
                                        handleDiscountChange(e.target.value);
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === '' || e.target.value === '.') {
                                            setDiscount(`${0}`);
                                            setIsDiscountValid(true);
                                            return;
                                        }

                                        setIsDiscountValid(MAX_TWO_NUMBERS_MAX_TWO_DECIMALS_REGEX.test(e.target.value));
                                    }}
                                    type="number"
                                    id="discount"
                                />
                            </div>
                        )}
                    </dt>
                </div>

                <hr />

                <div className={styles.row}>
                    <dd>Total</dd>
                    <dt>{!isInitialized ? <Skeleton className={styles.detailSkeleton} /> : totalDisplayValue}</dt>
                </div>
            </dl>

            <Modal.Actions>
                <Button
                    appearance="ghost"
                    onClick={handleSaveAsDraft}
                    isLoading={isSaveAsDraftLoading}
                    disabled={!isDiscountValid}
                >
                    Save as draft
                </Button>

                <Button
                    appearance="primary"
                    onClick={handleCreateAndSendInvoice}
                    isLoading={isCreateAndSendLoading}
                    disabled={!xeroContact?.email_address || !isDiscountValid}
                >
                    Approve & Send
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CreateAdminInvoiceModal;
